import languages from "@/data/languages.json";

export type LanguageTargetPageRecord = {
  targetCode: string;
  targetName: string;
  targetSlug: string;
  instructionCode: string;
  instructionName: string;
  path: string;
  title: string;
  description: string;
  intro: string;
  practiceSteps: string[];
  starterPrompts: string[];
  faqs: Array<{
    question: string;
    answer: string;
  }>;
  isPublishable: boolean;
  robots: { index: boolean; follow: boolean };
};

type LanguageEntry = {
  name: string;
  code: string;
  slug: string;
};

export const DEFAULT_LEARN_INSTRUCTION_LANGUAGE_CODE = "en-US";
export const DEFAULT_LEARN_INSTRUCTION_LANGUAGE_NAME = "US English";

const languageMap = languages as Record<string, string>;

const languageEntries: LanguageEntry[] = Object.entries(languageMap).map(([name, code]) => ({
  name,
  code,
  slug: slugifyLanguageName(name),
}));

const slugToLanguage = Object.fromEntries(
  languageEntries.map((entry) => [entry.slug, entry])
) as Record<string, LanguageEntry>;

function slugifyLanguageName(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/['\u2019]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function buildIntro(targetName: string): string {
  return `This page opens Chickytutor with ${targetName} selected as the target language and ${DEFAULT_LEARN_INSTRUCTION_LANGUAGE_NAME} selected as the instruction language. The goal is to start speaking ${targetName} quickly while keeping explanations, corrections, and setup friction in a familiar support language.`;
}

function buildPracticeSteps(targetName: string): string[] {
  return [
    `Start with a short ${targetName} voice session instead of another passive lesson.`,
    `Ask for corrections in ${DEFAULT_LEARN_INSTRUCTION_LANGUAGE_NAME}, then repeat the corrected ${targetName} phrase aloud.`,
    `Keep each session focused on one reusable task: introductions, ordering, directions, daily routines, or repair phrases.`,
  ];
}

function buildStarterPrompts(targetName: string): string[] {
  return [
    `Help me introduce myself in ${targetName}, then correct the most important mistake.`,
    `Roleplay ordering coffee in ${targetName}. Keep the replies short and practical.`,
    `Ask me simple questions about my day in ${targetName}, using ${DEFAULT_LEARN_INSTRUCTION_LANGUAGE_NAME} only when I need help.`,
    `Teach me repair phrases in ${targetName} so I can ask someone to repeat or slow down.`,
  ];
}

function buildFaqs(targetName: string) {
  return [
    {
      question: `Can I learn ${targetName} with Chickytutor?`,
      answer: `Yes. This page opens Chickytutor with ${targetName} selected so you can start a speaking session without manually choosing the target language first.`,
    },
    {
      question: `What instruction language does this page use?`,
      answer: `It uses ${DEFAULT_LEARN_INSTRUCTION_LANGUAGE_NAME} as the instruction language by default, so explanations and corrections are easy to follow while the speaking practice stays focused on ${targetName}.`,
    },
    {
      question: `Does this replace the language-pair pages?`,
      answer: `No. Use this page when you just want to learn ${targetName} with ${DEFAULT_LEARN_INSTRUCTION_LANGUAGE_NAME} support. Use the /from/ pages when the learner's support language is something else.`,
    },
  ];
}

export function getLanguageTargetPage(targetSlug: string): LanguageTargetPageRecord | null {
  const targetEntry = slugToLanguage[targetSlug];

  if (!targetEntry) {
    return null;
  }

  return {
    targetCode: targetEntry.code,
    targetName: targetEntry.name,
    targetSlug: targetEntry.slug,
    instructionCode: DEFAULT_LEARN_INSTRUCTION_LANGUAGE_CODE,
    instructionName: DEFAULT_LEARN_INSTRUCTION_LANGUAGE_NAME,
    path: `/learn/${targetEntry.slug}`,
    title: `Learn ${targetEntry.name} | Chickytutor`,
    description: `Open Chickytutor with ${targetEntry.name} selected and ${DEFAULT_LEARN_INSTRUCTION_LANGUAGE_NAME} as the instruction language for speaking-first practice.`,
    intro: buildIntro(targetEntry.name),
    practiceSteps: buildPracticeSteps(targetEntry.name),
    starterPrompts: buildStarterPrompts(targetEntry.name),
    faqs: buildFaqs(targetEntry.name),
    isPublishable: true,
    robots: {
      index: true,
      follow: true,
    },
  };
}

export function getPublishableLanguageTargetPages(): LanguageTargetPageRecord[] {
  return languageEntries
    .map((entry) => getLanguageTargetPage(entry.slug))
    .filter((page): page is LanguageTargetPageRecord => Boolean(page));
}

export function getRelatedPublishableLanguageTargets(
  record: LanguageTargetPageRecord,
  limit: number = 6
): LanguageTargetPageRecord[] {
  return getPublishableLanguageTargetPages()
    .filter((page) => page.path !== record.path)
    .sort((a, b) => a.title.localeCompare(b.title))
    .slice(0, limit);
}

export function getLanguageTargetStaticParams(): Array<{ target: string }> {
  return [];
}
